package com.narbase.letsgo.web.views.linkshare

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.weightOf
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.dimen
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.lifecycle.LifecycleOwner
import com.narbase.letsgo.dto.enum
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.models.linkshare.LinkShareDateRange
import com.narbase.letsgo.web.models.linkshare.ShareLinkParser
import com.narbase.letsgo.web.models.linkshare.TopLinksSortBy
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import kotlinx.browser.window
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.scrollable.scrollable
import com.narbase.letsgo.web.utils.table.headerCell
import com.narbase.letsgo.web.utils.table.listTable
import com.narbase.letsgo.web.utils.table.tableCell
import com.narbase.letsgo.web.utils.table.tableRow
import com.narbase.letsgo.web.utils.verticalSeparator
import com.narbase.letsgo.web.utils.views.*
import com.narbase.letsgo.web.utils.views.filters.FiltersView
import kotlin.js.Date

class LinkSharesManagementComponent : Component() {

    private val viewModel = LinkSharesManagementViewModel()

    private enum class Tab { TopLinks, Logs }

    private var defaultTab = Tab.TopLinks
    private var topLinksSection: View? = null
    private var logsSection: View? = null

    private var topLinksTableBody: View? = null
    private var logsTableBody: View? = null
    private var topLinksPagination: PaginationControls? = null
    private var logsPagination: PaginationControls? = null

    private var topLinksCustomRow: View? = null
    private var topLinksFromInput: TextInput? = null
    private var topLinksToInput: TextInput? = null

    private val upsertDialog = UpsertLinkShareRewardSettingDialog { }

    private val sharedByFilter = PassengerPickerFilter("Shared by".localized()) {
        viewModel.fSharedByPassengerId = it
        viewModel.applyLogFilters()
    }
    private val openedByFilter = PassengerPickerFilter("Opened by".localized()) {
        viewModel.fOpenedByPassengerId = it
        viewModel.applyLogFilters()
    }
    private val tokenFilter = ShareTokenFilter {
        viewModel.fShareToken = it
        viewModel.applyLogFilters()
    }
    private val finalStatusFilter = RideFinalStatusFilter {
        viewModel.fRideFinalStatus = it?.name
        viewModel.applyLogFilters()
    }

    override fun onViewMounted(lifecycleOwner: LifecycleOwner) {
        super.onViewMounted(lifecycleOwner)
        selectTab(defaultTab)
    }

    private fun selectTab(tab: Tab) {
        topLinksSection?.isVisible = tab == Tab.TopLinks
        logsSection?.isVisible = tab == Tab.Logs
        when (tab) {
            Tab.TopLinks -> viewModel.getTopLinks()
            Tab.Logs -> viewModel.getLogs()
        }
    }

    /** Settings is a single reward setting, not a list: open the dialog directly (edit if it exists, else create). */
    private fun openSettingsDialog() {
        viewModel.loadSingleSetting { existing ->
            if (existing != null) upsertDialog.edit(existing) else upsertDialog.add()
        }
    }

    override fun View?.getView() = view {
        style { matchParentDimensions }
        scrollable {
            style { matchParentDimensions }
            verticalLayout {
                style {
                    width = matchParent
                    height = wrapContent
                    padding = 32.px
                }

                horizontalLayout {
                    style { width = matchParent }
                    textView {
                        text = "Link Shares".localized()
                        style { width = wrapContent; fontSize = 20.px; fontWeight = "bold" }
                    }
                    horizontalFiller()
                }

                horizontalLayout {
                    style { width = matchParent; marginTop = 16.px }
                    theme.menuButton(this) {
                        text = "Settings".localized()
                        onClick = { openSettingsDialog() }
                    }
                    horizontalFiller(8)
                    theme.menuButton(this) {
                        text = "Top links".localized()
                        onClick = { selectTab(Tab.TopLinks) }
                    }
                    horizontalFiller(8)
                    theme.menuButton(this) {
                        text = "Logs".localized()
                        onClick = { selectTab(Tab.Logs) }
                    }
                }


                topLinksSection = topLinksSection().apply { isVisible = false }
                logsSection = logsSection().apply { isVisible = false }
            }
        }
    }

    private fun LinearLayout.topLinksSection() = verticalLayout {
        style { width = matchParent; marginTop = 16.px }

        horizontalLayout {
            style { width = matchParent; alignItems = Alignment.Center }

            textView { text = "Sort by".localized(); style { width = wrapContent; marginEnd = 8.px } }
            setupDropDownList(
                "Sort by",
                list = TopLinksSortBy.values().toList().toTypedArray(),
                itemToString = { if (it == TopLinksSortBy.CLICKS) "Clicks".localized() else "Rides completed".localized() },
                defaultValue = viewModel.topLinksSortBy,
                onItemSelected = { it?.let { v -> viewModel.topLinksSortBy = v } },
                viewWidth = 200.px,
            )

            horizontalFiller(16)

            textView { text = "Date".localized(); style { width = wrapContent; marginEnd = 8.px } }
            setupDropDownList(
                "Date",
                list = LinkShareDateRange.values().toList().toTypedArray(),
                itemToString = { dateRangeLabel(it) },
                defaultValue = viewModel.topLinksDateRange,
                onItemSelected = {
                    it?.let { v -> viewModel.topLinksDateRange = v }
                    topLinksCustomRow?.isVisible = it == LinkShareDateRange.CUSTOM
                },
                viewWidth = 200.px,
            )

            horizontalFiller(16)
            theme.smallOutlineButton(this) {
                text = "Apply".localized()
                style { marginStart = 8.px }
                onClick = { applyTopLinks() }
            }
        }

        topLinksCustomRow = horizontalLayout {
            style { width = matchParent; marginTop = 8.px }
            isVisible = false
            topLinksFromInput = filterInput("From yyyy-MM-dd".localized())
            topLinksToInput = filterInput("To yyyy-MM-dd".localized())
        }

        verticalLayout {
            matchParentDimensions()
            withLoadingAndError(
                viewModel.topLinksUiState,
                onRetryClicked = { viewModel.getTopLinks() },
                onLoaded = { onTopLinksLoaded() })

            topLinksTableBody = listTable {
                headerCell("Token".localized(), 2)
                headerCell("Shared By".localized(), 1)
                headerCell("Name".localized(), 2)
                headerCell("Clicks".localized(), 1)
                headerCell("Rides completed".localized(), 1)
                headerCell("Rewards".localized(), 1)
                headerCell("".localized(), 2)
            }
            topLinksPagination =
                setupPaginationControls(viewModel::getNextTopLinksPage, viewModel::getPreviousTopLinksPage)
        }
    }

    private fun applyTopLinks() {
        if (viewModel.topLinksDateRange == LinkShareDateRange.CUSTOM) {
            viewModel.topLinksCustomFrom = parseDate(topLinksFromInput?.text)
            viewModel.topLinksCustomTo = parseDate(topLinksToInput?.text)
        } else {
            viewModel.topLinksCustomFrom = null
            viewModel.topLinksCustomTo = null
        }
        viewModel.applyTopLinksFilters()
    }

    private fun setRewardsDisabled(shareToken: String, disabled: Boolean) {
        viewModel.setRewardsDisabled(shareToken, disabled) { viewModel.getTopLinks() }
    }

    private fun onTopLinksLoaded() {
        topLinksPagination?.update(viewModel.topLinksPageNo, viewModel.topLinksPageSize, viewModel.topLinksTotal)
        topLinksTableBody?.clearAllChildren()
        topLinksTableBody?.apply {
            viewModel.topLinks.forEachIndexed { index, item ->
                tableRow {
                    style { alignItems = Alignment.Center; paddingEnd = 8.px }
                    tableCell(item.shareToken, 2, 11.px)
                    tableCell(item.sharedByPassengerName ?: item.sharedByPassengerId.toString(), 1, 12.px)
                    tableCell(item.sharedName ?: item.sharedAddress ?: "-", 2, 12.px)
                    tableCell(item.clicks.toString(), 1, 14.px)
                    tableCell(item.ridesCompleted.toString(), 1, 14.px)
                    tableCell(
                        if (item.rewardsDisabled) "Disabled".localized() else "Enabled".localized(), 1, 12.px
                    )
                    tableCell(2) {
                        horizontalLayout {
                            style { alignItems = Alignment.Center; width = weightOf(1) }
                            theme.smallOutlineButton(this) {
                                text = "Copy link".localized()
                                onClick = { copyLink(item.shareToken, item.sharedLat, item.sharedLng) }
                            }
                            horizontalFiller(8)
                            theme.smallOutlineButton(this) {
                                text = if (item.rewardsDisabled) "Enable".localized() else "Disable".localized()
                                onClick = { setRewardsDisabled(item.shareToken, item.rewardsDisabled.not()) }
                            }
                        }
                    }
                }
                if (index != viewModel.topLinks.lastIndex) verticalSeparator()
            }
        }
    }

    private fun dateRangeLabel(range: LinkShareDateRange): String = when (range) {
        LinkShareDateRange.THIS_WEEK -> "This week".localized()
        LinkShareDateRange.THIS_MONTH -> "This month".localized()
        LinkShareDateRange.PAST_3_MONTHS -> "Past 3 months".localized()
        LinkShareDateRange.CUSTOM -> "Custom".localized()
    }

    private fun LinearLayout.logsSection() = verticalLayout {
        style { width = matchParent; marginTop = 16.px }

        horizontalLayout {
            style { width = matchParent; alignItems = Alignment.Center }
            mount(FiltersView(listOf(sharedByFilter, openedByFilter, tokenFilter, finalStatusFilter)))
        }

        verticalLayout {
            matchParentDimensions()
            withLoadingAndError(
                viewModel.logsUiState,
                onRetryClicked = { viewModel.getLogs() },
                onLoaded = { onLogsLoaded() })

            logsTableBody = listTable {
                headerCell("Id".localized(), 1)
                headerCell("Token".localized(), 2)
                headerCell("Shared By".localized(), 1)
                headerCell("Opened By".localized(), 1)
                headerCell("Type".localized(), 1)
                headerCell("Ride Created".localized(), 1)
                headerCell("Final Status".localized(), 1)
                headerCell("Rewarded".localized(), 1)
                headerCell("".localized(), 1)
            }
            logsPagination = setupPaginationControls(viewModel::getNextLogsPage, viewModel::getPreviousLogsPage)
        }
    }

    private fun onLogsLoaded() {
        logsPagination?.update(viewModel.logsPageNo, viewModel.logsPageSize, viewModel.logsTotal)
        logsTableBody?.clearAllChildren()
        logsTableBody?.apply {
            viewModel.logs.forEachIndexed { index, item ->
                tableRow {
                    style { alignItems = Alignment.Center; paddingEnd = 8.px }
                    tableCell(item.id.toString(), 1, 12.px)
                    tableCell(item.shareToken, 2, 11.px)
                    tableCell(item.sharedByPassengerName ?: "ID(${item.sharedByPassengerId})", 1, 12.px)
                    tableCell(
                        item.openedByPassengerName
                            ?: item.openedByPassengerId?.let { "ID(${it})" } ?: "-", 1, 12.px
                    )
                    tableCell(
                        item.sharedLocationType.enum<com.narbase.letsgo.web.models.linkshare.SharedLocationType>().name,
                        1,
                        11.px
                    )
                    tableCell(if (item.rideCreated) "Yes".localized() else "No".localized(), 1, 12.px)
                    tableCell(item.rideFinalStatus ?: "-", 1, 12.px)
                    tableCell(
                        if (item.openerRewardSent || item.sharerRewardSent || item.rideCompleterRewardSent)
                            "Yes".localized() else "No".localized(), 1, 12.px
                    )
                    tableCell(1) {
                        horizontalLayout {
                            style { alignItems = Alignment.Center; width = weightOf(1) }
                            theme.smallOutlineButton(this) {
                                text = "Copy link".localized()
                                onClick = { copyLink(item.shareToken, item.sharedLat, item.sharedLng) }
                            }
                        }
                    }
                }
                if (index != viewModel.logs.lastIndex) verticalSeparator()
            }
        }
    }

    private fun parseDate(text: String?): Double? {
        if (text.isNullOrBlank()) return null
        val millis = Date(text).getTime()
        return if (millis.isNaN()) null else millis
    }

    private fun copyLink(shareToken: String, lat: Double, lng: Double) {
        val url = ShareLinkParser.build(shareToken, lat, lng)
        window.navigator.clipboard.writeText(url)
        SnackBar.showText("Link copied".localized())
    }

    private fun LinearLayout.filterInput(placeholder: String): TextInput {
        val input = textInput {
            style {
                padding = "6px 12px".dimen()
                margin = 4.px
                border = "1px solid ${AppColors.borderColor}"
                borderRadius = 4.px
            }
        }
        input.element.setAttribute("placeholder", placeholder)
        return input
    }
}
